import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useCreateStakeholder } from "@/hooks/use-stakeholders";
import { cn } from "@/lib/utils";

interface AddStakeholderModalProps {
  solutionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ORGS: ReadonlyArray<{ id: "client" | "nymbl"; label: string }> = [
  { id: "client", label: "Client" },
  { id: "nymbl", label: "Nymbl" },
];

export function AddStakeholderModal(props: AddStakeholderModalProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="max-w-lg">{props.open && <ModalBody {...props} />}</DialogContent>
    </Dialog>
  );
}

function ModalBody({ solutionId, onOpenChange }: AddStakeholderModalProps) {
  const create = useCreateStakeholder(solutionId);
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [org, setOrg] = useState<"client" | "nymbl">("client");
  const [email, setEmail] = useState("");

  async function submit() {
    const trimmedName = name.trim();
    const trimmedRole = role.trim();
    const trimmedEmail = email.trim();
    if (!trimmedName || !trimmedRole) return;
    if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error("Enter a valid email or leave it blank.");
      return;
    }
    try {
      await create.mutateAsync({
        name: trimmedName,
        role: trimmedRole,
        org,
        email: trimmedEmail || null,
      });
      toast.success(`Added ${trimmedName}`);
      onOpenChange(false);
    } catch {
      // toasted by the mutation
    }
  }

  const canSubmit = name.trim().length > 0 && role.trim().length > 0 && !create.isPending;

  return (
    <>
      <DialogHeader>
        <DialogTitle className="font-display">Add a stakeholder</DialogTitle>
        <DialogDescription>
          People on the project who may not have a login — useful when you need to assign a question
          to a client contact.
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        <FieldLabel htmlFor="sk-name" label="Name" required>
          <Input
            id="sk-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Hank Vance"
            autoFocus
          />
        </FieldLabel>

        <FieldLabel htmlFor="sk-role" label="Role" required>
          <Input
            id="sk-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="e.g. VP of Sales"
          />
        </FieldLabel>

        <FieldLabel label="Org" required>
          <div className="inline-flex items-center rounded-md border border-[var(--color-border)] p-0.5 bg-card">
            {ORGS.map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => setOrg(o.id)}
                className={cn(
                  "px-3 py-1 text-xs font-medium rounded-[5px] transition-colors",
                  org === o.id ? "bg-nymbl-navy text-white" : "text-foreground/70 hover:bg-muted",
                )}
              >
                {o.label}
              </button>
            ))}
          </div>
        </FieldLabel>

        <FieldLabel htmlFor="sk-email" label="Email" help="Optional — for notifications later.">
          <Input
            id="sk-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="hank@client.com"
            onKeyDown={(e) => {
              if (e.key === "Enter" && canSubmit) submit();
            }}
          />
        </FieldLabel>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>
          Cancel
        </Button>
        <Button onClick={submit} disabled={!canSubmit}>
          {create.isPending ? "Adding…" : "Add stakeholder"}
        </Button>
      </DialogFooter>
    </>
  );
}

function FieldLabel({
  htmlFor,
  label,
  required,
  help,
  children,
}: {
  htmlFor?: string;
  label: string;
  required?: boolean;
  help?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        htmlFor={htmlFor}
        className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-semibold mb-1.5 block"
      >
        {label}
        {required && <span className="ml-0.5 text-destructive">*</span>}
      </label>
      {children}
      {help && <p className="text-[11px] text-muted-foreground mt-1">{help}</p>}
    </div>
  );
}
